
import React, { useEffect } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';

const WireframeObject: React.FC = () => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springX = useSpring(mouseX, { stiffness: 100, damping: 30 });
  const springY = useSpring(mouseY, { stiffness: 100, damping: 30 });

  const rotateX = useTransform(springY, [-500, 500], [20, -20]);
  const rotateY = useTransform(springX, [-500, 500], [-25, 25]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX - window.innerWidth / 2);
      mouseY.set(e.clientY - window.innerHeight / 2);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY]);

  return (
    <div className="relative w-[220px] h-[220px] sm:w-[320px] sm:h-[320px] lg:w-[500px] lg:h-[500px] flex items-center justify-center perspective-[1200px]">
      <motion.div
        style={{
          rotateX,
          rotateY,
          transformStyle: 'preserve-3d',
        }}
        className="relative w-48 h-48 sm:w-64 sm:h-64 lg:w-96 lg:h-96 flex items-center justify-center"
      >
        {/* Capa de Base: Grid Digital */}
        <div 
          className="absolute inset-0 border border-[#39FF14]/10 rounded-lg opacity-20"
          style={{ 
            transform: 'translateZ(-60px)',
            backgroundImage: 'linear-gradient(to right, #39FF14 1px, transparent 1px), linear-gradient(to bottom, #39FF14 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}
        />

        {/* Capa Central: Ventana de Interfaz "Explosionada" */}
        <motion.div
          style={{ transform: 'translateZ(0px)', transformStyle: 'preserve-3d' }}
          className="absolute inset-0 glass-panel border border-[#39FF14]/30 rounded-xl overflow-hidden shadow-[0_0_30px_rgba(57,255,20,0.1)]"
        >
          {/* Header de la ventana simulada */}
          <div className="h-4 sm:h-6 w-full border-b border-[#39FF14]/20 bg-[#39FF14]/5 flex items-center px-2 sm:px-3 gap-1 sm:gap-1.5">
            <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-[#39FF14]/40" />
            <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-[#39FF14]/40" />
            <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-[#39FF14]/40" />
            <div className="ml-1 sm:ml-2 w-10 sm:w-16 h-1 bg-[#39FF14]/10 rounded-full" />
          </div>
          
          {/* Contenido de la ventana (Bloques de UI) */}
          <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
            <div className="w-2/3 h-2 sm:h-3 bg-[#39FF14]/10 rounded-sm" />
            <div className="grid grid-cols-3 gap-1 sm:gap-2">
              <div className="h-6 sm:h-10 bg-[#39FF14]/5 rounded-sm border border-[#39FF14]/10" />
              <div className="h-6 sm:h-10 bg-[#39FF14]/5 rounded-sm border border-[#39FF14]/10" />
              <div className="h-6 sm:h-10 bg-[#39FF14]/5 rounded-sm border border-[#39FF14]/10" />
            </div>
            <div className="w-full h-8 sm:h-12 bg-[#39FF14]/5 rounded-sm border border-[#39FF14]/10 flex items-center justify-center">
              <div className="w-1/2 h-1 bg-[#39FF14]/20 rounded-full" />
            </div>
          </div>

          {/* Línea de escaneo vertical */}
          <motion.div 
            animate={{ left: ['-10%', '110%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            className="absolute top-0 bottom-0 w-12 sm:w-20 bg-gradient-to-r from-transparent via-[#39FF14]/10 to-transparent skew-x-12"
          />
        </motion.div>

        {/* Elementos Flotantes de UI (Primer Plano) */}
        
        {/* Botón Flotante 3D - Adaptado para pantallas pequeñas */}
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          className="absolute w-16 h-7 sm:w-24 sm:h-10 bg-[#39FF14] rounded shadow-[0_0_15px_#39FF14] flex items-center justify-center"
          style={{ 
            transform: 'translateZ(60px) translateX(-30px) translateY(-40px)',
            // Media queries logic handled via CSS classes for size, but Z-depth remains similar
          }}
        >
          <div className="w-8 sm:w-12 h-1 bg-black/20 rounded-full" />
        </motion.div>

        {/* Fragmento de Código Flotante */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute w-20 sm:w-32 p-2 sm:p-3 glass-panel border border-[#39FF14]/40 rounded shadow-lg"
          style={{ transform: 'translateZ(40px) translateX(50px) translateY(30px)' }}
        >
          <div className="space-y-1 sm:space-y-1.5">
            <div className="w-full h-1 bg-[#39FF14]/40 rounded-full" />
            <div className="w-4/5 h-1 bg-[#39FF14]/20 rounded-full" />
            <div className="w-full h-1 bg-[#39FF14]/40 rounded-full" />
          </div>
        </motion.div>

        {/* Partículas de Datos */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              z: [0, 60, 0],
              opacity: [0, 0.6, 0],
              x: [Math.sin(i) * 50, Math.sin(i) * 80],
              y: [Math.cos(i) * 50, Math.cos(i) * 80],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              ease: "linear",
              delay: Math.random() * 2
            }}
            className="absolute w-1 h-1 bg-[#39FF14] rounded-full shadow-[0_0_5px_#39FF14]"
            style={{ transformStyle: 'preserve-3d' }}
          />
        ))}

      </motion.div>

      {/* Brillo de ambiente optimizado */}
      <div className="absolute inset-0 bg-[#39FF14]/5 blur-[60px] sm:blur-[100px] rounded-full pointer-events-none opacity-50" />
    </div>
  );
};

export default WireframeObject;
